// PatientsTable.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Button,
  Popover,
  Typography,
  TextField,
  IconButton,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Autocomplete
} from '@mui/material';
import axios from 'axios';
import {
  fetchAnalysisResult,
  sendToProvider,
  streamSearchAuswertungen,
  fetchAnalysisCounts,
  fetchTopTitles,
  fetchTopTreatmentSteps
} from '../utils/api';
import { isRowActive, getRowColor } from '../utils/colorUtils';
import PatientTableView from '../components/PatientTableView';
import ResponseTableContainer from '../components/ResponseTableContainer';
import { LLM_COMBINATIONS, ROWS_PER_PAGE } from '../constants/config';
import { sanitizeColumnName, extractJsonByBrackets } from '../utils/parseModelResponse';
import { adaptSectionArrayToParsedObject } from '../utils/tableDataUtils';
import LoginModal from '../components/LoginModal';
import CloseIcon from '@mui/icons-material/Close';

const fetchAuswertungen = (limit, offset, searchTerm = '', title = '', filters = {}, treatmentStep = '') =>
  fetchAnalysisResult(limit, offset, searchTerm, title, filters, treatmentStep);

const escapeRegExp = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const renderHighlightedText = (text, query) => {
  if (!text) return text;
  const q = (query ?? '').trim();
  if (!q) return text;
  const re = new RegExp(`(${escapeRegExp(q)})`, 'iu');
  const parts = String(text).split(re);
  return parts.map((part, i) =>
    i % 2 === 1
      ? <mark key={i} style={{ backgroundColor: '#fff176', padding: '0 2px' }}>{part}</mark>
      : <React.Fragment key={i}>{part}</React.Fragment>
  );
};

const getCaretIndexWithin = (containerEl) => {
  const sel = window.getSelection?.();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!containerEl.contains(range.startContainer) && !containerEl.contains(range.endContainer)) return null;

  const preStart = range.cloneRange();
  preStart.selectNodeContents(containerEl);
  preStart.setEnd(range.startContainer, range.startOffset);
  const startIndex = preStart.toString().length;

  const preEnd = range.cloneRange();
  preEnd.selectNodeContents(containerEl);
  preEnd.setEnd(range.endContainer, range.endOffset);
  const endIndex = preEnd.toString().length;

  const mid = Math.floor((startIndex + endIndex) / 2);
  console.log('[PatientsTable Popover] startIndex:', startIndex, 'endIndex:', endIndex, 'mid:', mid);
  return mid;
};

const expandByDelimiters = (text, caret) => {
  if (!text || caret == null) {
    console.log('[expandByDelimiters] early-exit', { hasText: !!text, caret });
    return '';
  }

  const leftCommon = [
    -1,
    text.lastIndexOf(',', caret - 1),
    text.lastIndexOf(':', caret - 1),
    text.lastIndexOf(';', caret - 1),
    text.lastIndexOf('\n', caret - 1),
    text.lastIndexOf('\r', caret - 1),
    text.lastIndexOf('(', caret - 1),
  ];
  let left = Math.max(...leftCommon);

  const qLeft = text.lastIndexOf('?', caret - 1);
  let start = left + 1;
  if (qLeft > left) {
    start = qLeft + 1;
    if (text[start] === ' ') start++;
  }
  // " und "
  {
    const lo = (s) => s.toLowerCase();
    const fromIdx = left + 1;
    const searchStrLower = lo(text);
    const undNeedle = ' und ';
    const undLeftIdx = searchStrLower.lastIndexOf(undNeedle, caret - 1);
    if (undLeftIdx !== -1 && undLeftIdx >= fromIdx) {
      start = undLeftIdx + 1;
    }
  }

  const rightCommon = [
    text.indexOf(',', caret),
    text.indexOf(':', caret),
    text.indexOf(';', caret),
    text.indexOf('\n', caret),
    text.indexOf('\r', caret),
    text.indexOf(')', caret),
  ].filter(i => i !== -1);

  const dotSpaceRight = text.indexOf('. ', caret);
  if (dotSpaceRight !== -1) rightCommon.push(dotSpaceRight);

  let right = rightCommon.length ? Math.min(...rightCommon) : text.length;

  const qRight = text.indexOf('?', caret);
  if (qRight !== -1 && qRight < right) {
    right = qRight + 1;
  }
  // " und "
  {
    const lo = (s) => s.toLowerCase();
    const searchStrLower = lo(text);
    const undNeedle = ' und ';
    const undRightIdx = searchStrLower.indexOf(undNeedle, caret);
    if (undRightIdx !== -1 && undRightIdx < right) {
      right = undRightIdx;
    }
  }

  let slice = text.slice(start, right);

  slice = slice.trim();
  if (slice.startsWith('(')) slice = slice.slice(1).trimStart();
  if (slice.endsWith(')')) slice = slice.slice(0, -1).trimEnd();
  slice = slice.replace(/^[,;:]\s*/, '').replace(/\s*[,;:]$/, '');

  console.log('[expandByDelimiters]', { caret, left, right, slice });
  return slice;
};

const providersLabels = ['Analysis'] //['Gemini', 'OpenAI', 'Claude', 'DeepSeek', "Grok", "Kimi", "Glm"];
const hidedProviderLabels = [ 'Variant B' ] //['Variant A', 'Variant B', 'Variant C', 'Variant D', 'Variant E', 'Variant F', 'Variant G'];

const PatientsTable = () => {
  const [auswertungen, setAuswertungen] = useState([]);
  const [auswertungenRaw, setAuswertungenRaw] = useState([]);
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [responses, setResponses] = useState([]);
  const [responseTimes, setResponseTimes] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [activeTimers, setActiveTimers] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [username, setUsername] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [fullyAnalyzedCount, setFullyAnalyzedCount] = useState(0);
  const [partiallyAnalyzedCount, setPartiallyAnalyzedCount] = useState(0);
  const [isAnalysisLoading, setIsAnalysisLoading] = useState(false);
  const [showFullyAnalyzed, setShowFullyAnalyzed] = useState(true);
  const [showPartiallyAnalyzed, setShowPartiallyAnalyzed] = useState(true);
  const [fullyRows, setFullyRows] = useState([]);
  const [partiallyRows, setPartiallyRows] = useState([]);
  const [offset, setOffset] = useState(0);
  const [offsetHistory, setOffsetHistory] = useState([0]);
  const [selectedProviders, setSelectedProviders] = useState([...providersLabels]);
  const [userRole, setUserRole] = useState('');
  const currentPage = offsetHistory.length - 1;
  const [topTitles, setTopTitles] = useState([]);
  const [selectedTitle, setSelectedTitle] = useState(null);
  const [topTreatmentSteps, setTopTreatmentSteps] = useState([]);
  const [selectedTreatmentStep, setSelectedTreatmentStep] = useState(null);
  const [isRoleLoading, setIsRoleLoading] = useState(true);
  const [selectedText, setSelectedText] = useState('');
  const [lastViewSnapshot, setLastViewSnapshot] = useState(null);
  const [existingReview, setExistingReview] = useState(null);
  const [detailKey, setDetailKey] = useState(0);

  const fetchAssistantCommentsById = async (id) => {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/llm-analysis-result/column-json`,
      {
        params: { id },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      }
    );
    return res.data;
  };

  const handleSelectText = (txt) => {
    console.log('[Patient] setSelectedText:', JSON.stringify(txt));
    setSelectedText(txt);
  };

  const slimFullyRows = useMemo(
    () => (fullyRows || []).map(({ patnr_id, time }) => ({ patnr_id, time })),
    [fullyRows]
  );

  const slimPartiallyRows = useMemo(
    () => (partiallyRows || []).map(({ patnr_id, time }) => ({ patnr_id, time })),
    [partiallyRows]
  );

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchTopTreatmentSteps()
      .then((res) => setTopTreatmentSteps(res))
      .catch((err) => console.error("Error loading top treatment steps:", err));
  }, [isAuthenticated]);


  useEffect(() => {
    if (!isAuthenticated) return;
    fetchTopTitles()
      .then((res) => setTopTitles(res))
      .catch((err) => console.error("Error loading top titles:", err));
  }, [isAuthenticated]);


  const effectiveProvidersLabels = (userRole === 'admin' || userRole === 'user')
    ? providersLabels
    : hidedProviderLabels;
  
  useEffect(() => {
    setSelectedProviders(effectiveProvidersLabels);
    }, [userRole]);

  const handleProviderToggle = (label) => {
    setSelectedProviders((prev) =>
      prev.includes(label) ? prev.filter((p) => p !== label) : [...prev, label]
    );
  };

  useEffect(() => {
    setOffset(0);
    setOffsetHistory([0]);
  }, [showFullyAnalyzed, showPartiallyAnalyzed]);

  useEffect(() => {
    if (!isAuthenticated) return;
    setIsAnalysisLoading(true);

    fetchAnalysisCounts()
      .then(res => {
        //console.log('[fetchAnalysisCounts] res =', res);
        const fullyArray = Array.isArray(res.fully_analyzed) ? res.fully_analyzed : [];
        setFullyAnalyzedCount(fullyArray.length);
        setPartiallyAnalyzedCount(0);
        setFullyRows(fullyArray);
        setPartiallyRows([]);
      })
      .catch(err => {
        console.error('Error loading analysis counts:', err);
      })
      .finally(() => setIsAnalysisLoading(false));
  }, [isAuthenticated]);

  useEffect(() => {
    const checkToken = async () => {
      const token = localStorage.getItem("token");
      if (!token) return setIsAuthenticated(false);
      try {
        const res = await axios.get(process.env.REACT_APP_API_URL + "/check_session", {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.status === 200) setIsAuthenticated(true);
      } catch {
        setIsAuthenticated(false);
        localStorage.removeItem("token");
      }
    };
    checkToken();
    const interval = setInterval(checkToken, 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    axios
      .get(process.env.REACT_APP_API_URL + "/get_username", {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
      .then(res => {
        setUsername(res.data.username);
        setUserRole(res.data.role);
      })
      .catch(err => console.error("Failed to fetch username", err))
      .finally(() => setIsRoleLoading(false));
  }, [isAuthenticated]);

  const prefetchTitlesForRows = async (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return;

    cancelTitlePrefetch();
    const ctrl = new AbortController();
    titleAbortRef.current = ctrl;
    titleReqIdRef.current += 1;
    const reqId = titleReqIdRef.current;
    const { signal } = ctrl;

    const ids = Array.from(
      new Set(rows.map(getAnalysisId).filter(Boolean))
    );

    const missingIds = ids.filter((id) => {
      const r = rows.find(x => getAnalysisId(x) === id);
      const t = (r?.extracted_title ?? '').trim();
      return !t;
    });

    if (missingIds.length === 0) return;

    const token = localStorage.getItem('token');

    const CONCURRENCY = 12;
    const queue = [...missingIds];

    const worker = async () => {
      while (queue.length && !signal.aborted) {
        const id = queue.shift();
        try {
          const res = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/llm-analysis-result`,
            { id, limit: 1, offset: 0 },
            { signal, headers: { Authorization: `Bearer ${token}` } }
          );

          if (titleReqIdRef.current !== reqId) return;

          const rec = res?.data?.rows?.[0] ?? null;
          const title = (rec?.extracted_title ?? '').trim();
          if (!title) continue;

          setAuswertungen((prev) =>
            prev.map((r) => (getAnalysisId(r) === id ? { ...r, extracted_title: title } : r))
          );
        } catch (e) {
          if (signal.aborted) return;
          if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
          console.error('[prefetchTitlesForRows] error', e);
        }
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, queue.length) }, () => worker())
    );
  };

  const [bestPracticeMap, setBestPracticeMap] = useState({});

  const bestPracticeMapRef = useRef({});
  useEffect(() => { bestPracticeMapRef.current = bestPracticeMap; }, [bestPracticeMap]);

  const bestPracticeAbortRef = useRef(null);
  const bestPracticeReqIdRef = useRef(0);

  const cancelBestPracticePrefetch = () => {
    try { bestPracticeAbortRef.current?.abort(); } catch {}
    bestPracticeAbortRef.current = null;
  };
  useEffect(() => () => cancelBestPracticePrefetch(), []);

  const hasMeaningfulJson = (v) => {
    if (v == null) return false;

    if (typeof v === 'string') {
      const s = v.trim();
      if (!s) return false;
      if (s === 'null') return false;
      if (s === '{}' || s === '[]') return false;
      return s.length > 1;
    }

    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object') return Object.keys(v).length > 0;

    return false;
  };

  const fetchBestPracticeFlagById = async (id, { signal } = {}) => {
    const token = localStorage.getItem('token');

    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/llm-analysis-result`,
      { id, limit: 1, offset: 0 },
      { signal, headers: { Authorization: `Bearer ${token}` } }
    );

    const rec = res?.data?.rows?.[0] ?? null;
    console.log('[BP rec keys]', Object.keys(rec || {}));
    console.log('[BP value]', rec?.best_practice_template);
    const bp = rec?.best_practice_template ?? null;
    return hasMeaningfulJson(bp);
  };

  const prefetchBestPracticeForRows = async (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return;

    cancelBestPracticePrefetch();
    const ctrl = new AbortController();
    bestPracticeAbortRef.current = ctrl;
    bestPracticeReqIdRef.current += 1;
    const reqId = bestPracticeReqIdRef.current;
    const { signal } = ctrl;

    const ids = Array.from(new Set(rows.map(getAnalysisId).filter(Boolean)));

    const missingIds = ids.filter((id) => bestPracticeMapRef.current[id] === undefined);
    if (missingIds.length === 0) return;

    const CONCURRENCY = 12;
    const queue = [...missingIds];

    const worker = async () => {
      while (queue.length && !signal.aborted) {
        const id = queue.shift();
        try {
          const has = await fetchBestPracticeFlagById(id, { signal });

          if (bestPracticeReqIdRef.current !== reqId) return;

          setBestPracticeMap((prev) => {
            if (prev[id] !== undefined) return prev;
            return { ...prev, [id]: has };
          });
        } catch (e) {
          if (signal.aborted) return;
          if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
          console.error('[prefetchBestPracticeForRows] error', e);
          setBestPracticeMap((prev) => (prev[id] !== undefined ? prev : { ...prev, [id]: false }));
        }
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, queue.length) }, () => worker())
    );
  };

  useEffect(() => {
    if (!isStreaming && isAuthenticated && searchTerm === '') {
      setInitialLoading(true);
      //console.log("[useEffect] selectedTreatmentStep:", selectedTreatmentStep);
      const treatmentStepValue = selectedTreatmentStep
        ? (typeof selectedTreatmentStep === 'string'
            ? selectedTreatmentStep
            : selectedTreatmentStep.treatment_step || '')
        : '';
      //console.log("[useEffect] treatmentStepValue:", treatmentStepValue);
      fetchAnalysisResult(ROWS_PER_PAGE, offset, '', selectedTitle ? selectedTitle.title : '', {
        fullyRows: slimFullyRows,
        partiallyRows: slimPartiallyRows,
        showFullyAnalyzed,
        showPartiallyAnalyzed
      }, treatmentStepValue)
        .then(res => {
          const rows = res.data.rows || res.data;
          setAuswertungen(rows);
          setTotalCount(res.data.total ?? (res.data.rows?.length ?? 0));
          prefetchTitlesForRows(rows);
          prefetchBestPracticeForRows(rows);
        })
        .catch(console.error)
        .finally(() => setInitialLoading(false));
    }
  }, [offset, isAuthenticated, searchTerm, isStreaming, showFullyAnalyzed, showPartiallyAnalyzed]);

  const titleAbortRef = useRef(null);
  const titleReqIdRef = useRef(0);

  const cancelTitlePrefetch = () => {
    try { titleAbortRef.current?.abort(); } catch {}
    titleAbortRef.current = null;
  };

  useEffect(() => {
    return () => cancelTitlePrefetch(); // cleanup
  }, []);

  const getAnalysisId = (row) => {
    const id = Number(row?.id ?? row?.llm_analysis_result_id);
    return Number.isFinite(id) ? id : null;
  };

  const listAbortRef = useRef(null);
  const listReqIdRef = useRef(0);

  const cancelListRequest = () => {
    try { listAbortRef.current?.abort(); } catch {}
    listAbortRef.current = null;
  };

  const startListRequest = () => {
    cancelListRequest();
    const ctrl = new AbortController();
    listAbortRef.current = ctrl;
    listReqIdRef.current += 1;
    const reqId = listReqIdRef.current;
    return { signal: ctrl.signal, reqId };
  };

  const isCanceled = (e) =>
    e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError';

  const handleNext = async () => {
    console.log("[handleNext] selectedTreatmentSteps", selectedTreatmentStep);
    const treatmentStepValue = selectedTreatmentStep
      ? (typeof selectedTreatmentStep === 'string'
          ? selectedTreatmentStep
          : selectedTreatmentStep.treatment_step || '')
      : '';

    const { signal, reqId } = startListRequest();

    try {
      const res = await fetchAnalysisResult(
        ROWS_PER_PAGE,
        offset,
        '',
        selectedTitle ? selectedTitle.title : '',
        {
          fullyRows: slimFullyRows,
          partiallyRows: slimPartiallyRows,
          showFullyAnalyzed,
          showPartiallyAnalyzed
        },
        treatmentStepValue,
        { signal }
      );

      if (listReqIdRef.current !== reqId) return;

      const rowsFromServer = res.data.rows || res.data;
      setAuswertungen(rowsFromServer);
      const total = res.data.total ?? rowsFromServer.length;
      setTotalCount(total);
      prefetchTitlesForRows(rowsFromServer);
      prefetchBestPracticeForRows(rowsFromServer);
      const next = res.data.nextOffset;
      if (next !== undefined && next !== null) {
        setOffsetHistory(prev => [...prev, next]);
        setOffset(next);
      }
    } catch (e) {
      if (!isCanceled(e)) console.error(e);
    }
  };

  const handlePrev = () => {
    if (offsetHistory.length <= 1) return;
    const newHistory = [...offsetHistory];
    newHistory.pop();
    const prevOffset = newHistory[newHistory.length - 1] || 0;
    setOffsetHistory(newHistory);
    setOffset(prevOffset);
  };

  useEffect(() => {
    if (!isStreaming) return;
    setSelectedTreatmentStep(null);
    const start = currentPage * ROWS_PER_PAGE;
    setAuswertungen(auswertungenRaw.slice(start, start + ROWS_PER_PAGE));
    fetchAnalysisResult(ROWS_PER_PAGE, start, searchTerm, selectedTitle ? selectedTitle.title : '')
      .then(res => {
        const data = res.data.rows || res.data;
        const total = res.data.total ?? data.length;
        setAuswertungen(data);
        setTotalCount(total);
      })
      .finally(() => setInitialLoading(false));
  }, [auswertungenRaw, currentPage, isStreaming]);

  const parseMaybeJson = (x) => {
    if (x == null) return '';
    if (typeof x === 'string') {
      const s = x.trim();
      if (s.startsWith('{') || s.startsWith('[')) {
        try { return JSON.parse(s); } catch { return s; }
      }
      return s;
    }
    return x;
  };

  const buildResponseObject = ({ crRaw, extracted_title, description }) => {
    const crParsed = parseMaybeJson(crRaw);
    const titleStr = typeof extracted_title === 'string'
      ? extracted_title.trim()
      : (extracted_title ?? '');
    const descStr = typeof description === 'string'
      ? description.trim()
      : (description ?? '');
    const out = {
      title: titleStr || '',
      description: descStr || '',
      comparison_result: crParsed ?? null,
    };
    //console.log('[buildResponseObject] out =', out);
    return out;
  };

  const handleRowClick = async (event, row) => {
    setLastViewSnapshot({
      offset,
      offsetHistory: [...offsetHistory],
      patnr_id: row.patnr_id,
      time: row.time
    });
    const analysisId = Number(row?.id ?? row?.llm_analysis_result_id);
    setExistingReview(null);
    setSelectedRow({
      ...row,
      analysisId,
    });
    setPopoverAnchor(event.currentTarget);
    const initialResponses = [''];
    setResponses(initialResponses);
    setResponseTimes([0]);
    setActiveTimers([0]);
    let builtResponseForLLM = '';
    try {
      if (Number.isFinite(analysisId)) {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/llm-analysis-result`,
          { id: analysisId, limit: 1, offset: 0 },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        const rec = res?.data?.rows?.[0] ?? null;
        const crRaw            = rec?.comparison_result ?? row?.comparison_result ?? null;
        const extracted_title  = rec?.extracted_title   ?? row?.extracted_title   ?? null;
        const description      = rec?.description       ?? row?.description       ?? null;
        builtResponseForLLM = buildResponseObject({
          crRaw,
          extracted_title,
          description,
        });
      } else {
        builtResponseForLLM = parseMaybeJson(row?.comparison_result ?? '');
      }
    } catch (e) {
      console.error('[PatientsTable] /api/llm-analysis-result failed, fallback', e);
      builtResponseForLLM = parseMaybeJson(row?.comparison_result ?? '');
    }
    const isAlreadyReviewed = Array.isArray(fullyRows)
      ? fullyRows.some(fr => fr.id === analysisId)
      : false;

    if (isAlreadyReviewed && Number.isFinite(analysisId)) {
      try {
        const reviewData = await fetchAssistantCommentsById(analysisId);
        setExistingReview(reviewData);
      } catch (err) {
        console.error('[PatientsTable] failed to load existing review for id', analysisId, err);
        setExistingReview(null);
      }
    } else {
      setExistingReview(null);
    }
    initialResponses[0] = builtResponseForLLM;
    setResponses([...initialResponses]);
  };

  const handleRowClickGuarded = async (event, row) => {
    const analysisId = getAnalysisId(row);
    const anchorEl = event?.currentTarget;

    if (Number.isFinite(analysisId) && bestPracticeMapRef.current[analysisId] === undefined) {
      try {
        const has = await fetchBestPracticeFlagById(analysisId);
        setBestPracticeMap((prev) => (prev[analysisId] !== undefined ? prev : { ...prev, [analysisId]: has }));
      } catch (e) {
        console.error('[handleRowClickGuarded] best_practice check failed', e);
        setBestPracticeMap((prev) => (prev[analysisId] !== undefined ? prev : { ...prev, [analysisId]: false }));
      }
    }

    return handleRowClick({ currentTarget: anchorEl }, row);
  };


  const handleClose = async () => {
    cancelTitlePrefetch();
    setSelectedRow(null);
    setPopoverAnchor(null);
    setResponses([]);
    setResponseTimes([]);
    let effectiveOffset = offset;
    let effectiveOffsetHistory = offsetHistory;
    if (lastViewSnapshot) {
      effectiveOffset = lastViewSnapshot.offset;
      effectiveOffsetHistory = lastViewSnapshot.offsetHistory;
      setOffset(effectiveOffset);
      setOffsetHistory(effectiveOffsetHistory);
    }

    if (!isStreaming) {
      setInitialLoading(true);
      setIsAnalysisLoading(true);
      try {
        const countsRes = await fetchAnalysisCounts();
        console.log('[handleClose] fresh analysis_counts:', countsRes);
        const fullyArray =
          Array.isArray(countsRes.fully_rows)
            ? countsRes.fully_rows
            : Array.isArray(countsRes.fully_analyzed)
              ? countsRes.fully_analyzed
              : [];
        setFullyAnalyzedCount(fullyArray.length);
        setPartiallyAnalyzedCount(0);
        setFullyRows(fullyArray);
        setPartiallyRows([]);
        const treatmentStepValue = selectedTreatmentStep
          ? (typeof selectedTreatmentStep === 'string'
              ? selectedTreatmentStep
              : selectedTreatmentStep.treatment_step || '')
          : '';
        const listRes = await fetchAnalysisResult(
          ROWS_PER_PAGE,
          effectiveOffset,
          '',
          selectedTitle ? selectedTitle.title : '',
          {
            fullyRows: fullyArray.map(r => ({
              id: r.id,
              patnr_id: r.patnr,
              time: r.created_at
            })),
            partiallyRows: [],
            showFullyAnalyzed,
            showPartiallyAnalyzed
          },
          treatmentStepValue
        );
        const newRows = listRes.data.rows || listRes.data;
        const newTotal = listRes.data.total ?? (newRows?.length ?? 0);
        setAuswertungen(newRows);
        setTotalCount(newTotal);
        prefetchTitlesForRows(newRows);
        prefetchBestPracticeForRows(newRows);
      } catch (err) {
        console.error('[handleClose] refresh failed:', err);
      } finally {
        setInitialLoading(false);
        setIsAnalysisLoading(false);
      }
    }
  };

  const filteredAuswertungen = useMemo(() => {
    const isInList = (row, list) =>
      list.some(item => item.patnr_id === row.patnr_id && item.time === row.time);
    return auswertungen.filter(row => {
      const isFully = isInList(row, fullyRows);
      const isPartial = isInList(row, partiallyRows);
      if (!isFully && !isPartial) {
        return true;
      }
      if (showFullyAnalyzed && showPartiallyAnalyzed) {
        return true;
      }
      if (showFullyAnalyzed && !showPartiallyAnalyzed) {
        return isFully;
      }
      if (!showFullyAnalyzed && showPartiallyAnalyzed) {
        return isPartial;
      }
      return false;
    });
  }, [auswertungen, showFullyAnalyzed, showPartiallyAnalyzed, fullyRows, partiallyRows]);

  const normalizeInfo = (s) =>
    String(s ?? '')
      .trim();

  const popoverInfo = normalizeInfo(selectedRow?.info ?? '');

  const goToFirstPage = () => {
    setOffset(0);
    setOffsetHistory([0]);
  };

  const runSearch = async () => {
    cancelListRequest();
    goToFirstPage();
    const term = searchInput.trim();
    setSearchTerm(term);
    setIsStreaming(true);
    setInitialLoading(true);
    setAuswertungenRaw([]);
    setAuswertungen([]);
    setTotalCount(0);

    await new Promise((r) => setTimeout(r, 0));

    streamSearchAuswertungen(term, (row, count) => {
      setAuswertungenRaw(prev => [...prev, row]);
      setTotalCount(count);
    }).finally(() => setInitialLoading(false));
  };

  const runResetFilter = async () => {
    const { signal, reqId } = startListRequest();

    setSearchInput('');
    setSearchTerm('');
    setIsStreaming(false);
    setAuswertungenRaw([]);

    setOffset(0);
    setOffsetHistory([0]);

    setInitialLoading(true);
    try {
      const res = await fetchAnalysisResult(
        ROWS_PER_PAGE,
        0,
        '',
        '',
        {
          fullyRows: slimFullyRows,
          partiallyRows: slimPartiallyRows,
          showFullyAnalyzed,
          showPartiallyAnalyzed
        },
        '',
        { signal }
      );

      if (listReqIdRef.current !== reqId) return;

      const rows = res.data.rows || res.data;
      setAuswertungen(rows);
      setTotalCount(res.data.total ?? (res.data.rows?.length ?? 0));
      prefetchTitlesForRows(rows);
      prefetchBestPracticeForRows(rows);
    } catch (e) {
      if (!isCanceled(e)) console.error(e);
    } finally {
      if (listReqIdRef.current === reqId) setInitialLoading(false);
    }
  };

  const currentPageNumber = offsetHistory.length;
  const totalPages = useMemo(() => {
    const n = Number(totalCount) || 0;
    return Math.max(1, Math.ceil(n / ROWS_PER_PAGE));
  }, [totalCount]);

  const hasBestPracticeForRow = (row) => {
    const id = getAnalysisId(row);
    return Boolean(id && bestPracticeMap[id]);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Button
        variant="outlined"
        onClick={() => {
          localStorage.removeItem("token");
          setIsAuthenticated(false);
        }}
        sx={{ position: 'absolute', top: 10, right: 10 }}
      >
        Logout
      </Button>

      {/* {!isRoleLoading && isAuthenticated && userRole !== 'user' && (
        <Box
          sx={{
            position: 'absolute',
            top: 60,
            right: 10,
            width: 300,
            border: '1px solid #ccc',
            borderRadius: 2,
            backgroundColor: '#fafafa',
            p: 2
          }}
        >
          { <Typography variant="h6" sx={{ mb: 1 }}>
            { (userRole === 'admin' || userRole === 'moderator')
                ? 'Top 20 Titles (Gemini)'
                : 'Top 20 Titles (Variant A)' }
          </Typography> }
          {topTitles.length === 0 ? (
            <Typography variant="body2" color="textSecondary">No data</Typography>
          ) : (
            <Autocomplete
              options={topTitles}
              value={selectedTitle}
              getOptionLabel={(option) => `${option.title} — ${option.count}`}
              isOptionEqualToValue={(option, value) => option.title === value.title}
              onChange={(event, newValue) => {
                setOffset(0);
                setOffsetHistory([0]);
                setInitialLoading(true);
                setSelectedTreatmentStep(null);

                if (newValue) {
                  setSelectedTitle(newValue);
                  fetchAnalysisResult(ROWS_PER_PAGE, 0, '', newValue.title, {
                    fullyRows: slimFullyRows,
                    partiallyRows: slimPartiallyRows,
                    showFullyAnalyzed,
                    showPartiallyAnalyzed
                  })
                    .then(res => {
                      const rows = Array.isArray(res.data.rows)
                        ? res.data.rows
                        : (Array.isArray(res.data) ? res.data : []);
                      setAuswertungen(rows);
                      setTotalCount(res.data.total ?? (res.data.rows?.length ?? 0));
                    })
                    .catch(console.error)
                    .finally(() => setInitialLoading(false));
                } else {
                  setSelectedTitle(null);
                  fetchAnalysisResult(ROWS_PER_PAGE, 0, '', '', {
                    fullyRows: slimFullyRows,
                    partiallyRows: slimPartiallyRows,
                    showFullyAnalyzed,
                    showPartiallyAnalyzed
                  })
                    .then(res => {
                      const rows = Array.isArray(res.data.rows)
                        ? res.data.rows
                        : (Array.isArray(res.data) ? res.data : []);
                      setAuswertungen(rows);
                      setTotalCount(res.data.total ?? (res.data.rows?.length ?? 0));
                    })
                    .catch(console.error)
                    .finally(() => setInitialLoading(false));
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Title"
                  variant="outlined"
                  size="small"
                />
              )}
              sx={{ mt: 1 }}
            />
          )}
      </Box>
    )}
    {!isRoleLoading && isAuthenticated && userRole !== 'user' && (
      <Box
        sx={{
          position: 'absolute',
          top: 185,
          right: 10,
          width: 300,
          border: '1px solid #ccc',
          borderRadius: 2,
          backgroundColor: '#fafafa',
          p: 2
        }}
        >
        { <Typography variant="h6" sx={{ mb: 1 }}>
          { (userRole === 'admin' || userRole === 'moderator')
              ? 'Top 20 Treatment Steps'
              : 'Top 20 Treatment Steps' }
        </Typography> }
        {topTreatmentSteps.length === 0 ? (
          <Typography variant="body2" color="textSecondary">No data</Typography>
        ) : (
          <Autocomplete
            options={topTreatmentSteps}
            value={selectedTreatmentStep || null}
            getOptionLabel={(option) => {
              if (!option) return '';
              const ts = option.treatment_step;
              if (ts && typeof ts === 'object') {
                return `${ts.step} — ${option.count}`;
              }
              if (typeof ts === 'string') {
                return `${ts} — ${option.count}`;
              }
              return '';
            }}
            isOptionEqualToValue={(option, value) =>
              option?.treatment_step === value?.treatment_step
            }onChange={(event, newValue) => {
              setOffset(0);
              setOffsetHistory([0]);
              setInitialLoading(true);
              setSelectedTitle(null)

              if (newValue) {
                setSelectedTreatmentStep(newValue);
                fetchAnalysisResult(ROWS_PER_PAGE, 0, '', selectedTitle ? selectedTitle.title : '', {
                  fullyRows: slimFullyRows,
                  partiallyRows: slimPartiallyRows,
                  showFullyAnalyzed,
                  showPartiallyAnalyzed
                }, newValue.treatment_step)
                  .then(res => {
                    const rows = Array.isArray(res.data.rows)
                      ? res.data.rows
                      : (Array.isArray(res.data) ? res.data : []);
                    setAuswertungen(rows);
                    setTotalCount(res.data.total ?? (res.data.rows?.length ?? 0));
                  })
                  .catch(console.error)
                  .finally(() => setInitialLoading(false));
              } else {
                setSelectedTreatmentStep(null);
                fetchAnalysisResult(ROWS_PER_PAGE, 0, '', selectedTitle ? selectedTitle.title : '', {
                  fullyRows: slimFullyRows,
                  partiallyRows: slimPartiallyRows,
                  showFullyAnalyzed,
                  showPartiallyAnalyzed
                }, '')
                  .then(res => {
                    const rows = Array.isArray(res.data.rows)
                      ? res.data.rows
                      : (Array.isArray(res.data) ? res.data : []);
                    setAuswertungen(rows);
                    setTotalCount(res.data.total ?? (res.data.rows?.length ?? 0));
                  })
                  .catch(console.error)
                  .finally(() => setInitialLoading(false));
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Treatment Step"
                variant="outlined"
                size="small"
              />
            )}
            sx={{ mt: 1 }}
          />
        )}
      </Box>
    )} */}
      <LoginModal open={!isAuthenticated} onLoginSuccess={() => setIsAuthenticated(true)} />

        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          alignItems: 'flex-start',
          justifyContent: 'space-between'
        }}>
          {!isRoleLoading && isAuthenticated && userRole !== 'user' && (
            <Box sx={{ minWidth: '200px' }}>
              { <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showFullyAnalyzed}
                      onChange={(e) => {
                        const newVal = e.target.checked;
                        const treatmentStepValue =
                          selectedTreatmentStep
                            ? (typeof selectedTreatmentStep === 'string'
                                ? selectedTreatmentStep
                                : selectedTreatmentStep.treatment_step || '')
                            : '';
                        setShowFullyAnalyzed(newVal);
                        setOffset(0);
                        setOffsetHistory([0]);
                        setInitialLoading(true);
                        fetchAnalysisResult(
                          ROWS_PER_PAGE,
                          offset,
                          '',
                          selectedTitle ? selectedTitle.title : '',
                          {
                            fullyRows: slimFullyRows,
                            partiallyRows: slimPartiallyRows,
                            showFullyAnalyzed,
                            showPartiallyAnalyzed
                          },
                          treatmentStepValue
                        )
                          .then(res => {
                            const rows = Array.isArray(res.rows) ? res.rows : [];
                            setAuswertungen(rows);
                            setTotalCount(
                              typeof res.total === 'number'
                                ? res.total
                                : rows.length
                            );
                          })
                          .catch(console.error)
                          .finally(() => setInitialLoading(false));
                      }}
                      sx={{
                        color: showFullyAnalyzed ? 'green' : '#a5d6a7',
                        '&.Mui-checked': {
                          color: 'green',
                        },
                      }}
                    />
                  }
                  label={`${fullyAnalyzedCount} - Already corrected`}
                  sx={{
                    color: showFullyAnalyzed ? 'green' : '#66bb6a',
                    fontWeight: showFullyAnalyzed ? 'bold' : 'normal',
                  }}
                />
                {
                  //<FormControlLabel
                  // control={
                  //   <Checkbox
                  //     checked={showPartiallyAnalyzed}
                  //     onChange={(e) => {
                  //       const newVal = e.target.checked;
                  //       setShowPartiallyAnalyzed(newVal);
                  //       setOffset(0);
                  //       setOffsetHistory([0]);
                  //       setInitialLoading(true);
                  //       fetchAnalysisResult(ROWS_PER_PAGE, 0, '', selectedTitle ? selectedTitle.title : '', {
                  //         fullyRows: slimFullyRows,
                  //         partiallyRows: slimPartiallyRows,
                  //         showFullyAnalyzed,
                  //         showPartiallyAnalyzed: newVal,
                  //       }).then(res => {
                  //         setAuswertungen(res.data.rows || res.data);
                  //         setTotalCount(res.data.total ?? (res.data.rows?.length ?? 0));
                  //       }).finally(() => setInitialLoading(false));
                  //     }}
                  //     sx={{
                  //       color: showPartiallyAnalyzed ? 'orange' : '#ffcc80',
                  //       '&.Mui-checked': {
                  //         color: 'orange',
                  //       },
                  //     }}
                  //   />
                  // }
                  //label={`${partiallyAnalyzedCount} - Partially Analyzed`}
                  // sx={{
                  //   color: showPartiallyAnalyzed ? 'orange' : '#ffa726',
                  //   fontWeight: showPartiallyAnalyzed ? 'bold' : 'normal',
                  // }}
                ///>
                }
              </FormGroup>}
            </Box>
          )}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            margin: '0 auto'
          }}>
            <TextField
              label="Treatment"
              variant="outlined"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  runSearch();
                }
              }}
              sx={{ width: 400 }}
            />
            <Button
              variant="contained"
              onClick={runSearch}
            >
              Search
            </Button>
            {searchTerm !== '' && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={runResetFilter}
              >
                Reset filter
              </Button>
            )}
          </Box>
          <Box sx={{ minWidth: '200px' }} />
        </Box>
      <Typography variant="body2" color="textSecondary" textAlign="center" sx={{ mb: 2 }}>
        Treatments found: {totalCount}
      </Typography>
      {!isRoleLoading && isAuthenticated && userRole !== 'user' && (
        <Typography variant="body2" color="textSecondary" textAlign="center" sx={{ mb: 1 }}>
          Already corrected: {fullyAnalyzedCount}
          {/* | Partially analyzed: {partiallyAnalyzedCount} */}
        </Typography>
      )}
      {isAnalysisLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 1 }}>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          <Typography variant="body2" color="textSecondary">
            Analyzing all records...
          </Typography>
        </Box>
      )}

      <Box sx={{ flexGrow: 1, mt: 5 }}>
        {initialLoading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <PatientTableView
            data={filteredAuswertungen}
            auswertungen={auswertungen}
            onRowClick={handleRowClickGuarded}
            getRowColor={row => getRowColor(row, fullyRows)}
            isRowActive={row => isRowActive(row, auswertungen)}
            isStreaming={isStreaming}
            isTitleChoice={Boolean(selectedTitle)}
            isTreatmentStepsChoice={Boolean(selectedTreatmentStep)}
            onSelectText={handleSelectText}
            selectedText={selectedText}
            hasBestPractice={(row) => {
              const id = getAnalysisId(row);
              return Boolean(id && bestPracticeMap[id]);
            }}
          />
        )}
      </Box>

      <Box sx={{
        position: 'sticky',
        bottom: 0,
        backgroundColor: '#fff',
        borderTop: '1px solid #ccc',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 2
      }}>
        <Button
          variant="outlined"
          onClick={handlePrev}
          disabled={currentPage === 0}
          sx={{ mr: 2 }}
        >
          ◀ Prev
        </Button>
        <Typography variant="body2">
          Page {currentPageNumber} of {totalPages}
        </Typography>
        <Button
          variant="outlined"
          onClick={handleNext}
          disabled={currentPageNumber >= totalPages}
          sx={{ ml: 2 }}
        >
          Next ▶
        </Button>
      </Box>

      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          sx: {
            width: '95vw',
            height: '100vh',
            p: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'row',
            position: 'relative',
          }
        }}
      >
        {/* LEFT COLUMN: text/info + providers */}
        <Box
          sx={{
            width: { xs: '100%', md: '36%' },
            maxWidth: { md: 560 },
            minWidth: { md: 420 },
            height: '100%',
            borderRight: { md: '1px solid #eee' },
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#fff',
          }}
        >
          {/* sticky header left: spacer to align with right header */}
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 3000,
              backgroundColor: '#fff',
              borderBottom: '1px solid #eee',
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            {/* same visual height as IconButton (40x40) */}
            <IconButton size="small" sx={{ visibility: 'hidden' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* left scrollable content */}
          <Box sx={{ p: 2, overflowY: 'auto' }}>
            {/* info text */}
            <Box
              sx={{
                border: '1px solid #ccc',
                borderRadius: '12px',
                p: 2,
                backgroundColor: '#f9f9f9',
                overflowWrap: 'break-word',
              }}
              onDoubleClick={(e) => {
                const text = popoverInfo;
                console.log('[PatientsTable Popover] text.len=', text.length);
                const caret = getCaretIndexWithin(e.currentTarget);
                if (caret == null) return;
                const phrase = expandByDelimiters(text, caret);
                console.log('[PatientsTable Popover] final phrase:', JSON.stringify(phrase));
                if (phrase) handleSelectText(phrase);
              }}
            >
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {renderHighlightedText(popoverInfo, selectedText)}
              </Typography>
            </Box>

            {/* providers box
            <Box
              sx={{
                mt: 2,
                border: '1px solid #ccc',
                borderRadius: 2,
                p: 1.5,
                backgroundColor: '#fafafa',
              }}
            >
              <Typography sx={{ fontWeight: 'bold', mb: 1 }}>
                {userRole === 'user' ? 'Variants' : 'Providers'}
              </Typography>
              {effectiveProvidersLabels.map((label) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <input
                    type="checkbox"
                    checked={selectedProviders.includes(label)}
                    onChange={() => handleProviderToggle(label)}
                    style={{ marginRight: 8 }}
                  />
                  <Typography sx={{ fontSize: '0.9rem' }}>{label}</Typography>
                </Box>
              ))}
            </Box> */}
          </Box>
        </Box>

        {/* RIGHT COLUMN: response table */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            height: '100%',
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            backgroundColor: '#fff',
          }}
        >
          {/* sticky header right: close button */}
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 3000,
              backgroundColor: '#fff',
              borderBottom: '1px solid #eee',
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {selectedRow && (
              <ResponseTableContainer
                analysisId={selectedRow.analysisId}
                responses={responses}
                providersLabels={effectiveProvidersLabels}
                selectedProviders={selectedProviders}
                providerColumns={LLM_COMBINATIONS.map(([providerKey, providerModel]) =>
                  sanitizeColumnName(providerKey, providerModel)
                )}
                providerLabels={LLM_COMBINATIONS.map(([providerKey]) => providerKey)}
                patnr_id={selectedRow.id}
                time={selectedRow.time}
                responseTimes={responseTimes}
                activeTimers={activeTimers}
                selectedText={selectedText}
                onSelectText={handleSelectText}
                info={normalizeInfo(`${selectedRow.info ?? ''}; ${selectedRow.kommentar ?? ''}`)}
                existingReview={existingReview}
              />
            )}
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default PatientsTable;